import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';
import _ from 'underscore';

import {
  OBJECT_TYPE_TO_CHILD_DISPLAY_TEXT,
  OBJECT_TYPE_TO_REMOVED_CHILD_CLASS,
  ObjectTypeWithChild,
} from 'config-migration/import/ObjectSectionConfig';
import {areActionsEqual} from 'config-migration/import/planning-logic/convertImportIdentifiersToPlans';
import {
  BaseMigrationObjectPlan,
  HighImpactChange,
  MigrationObjectPlan,
} from 'config-migration/MigrationConstants';
import {HighImpactChangeIcon} from 'config-migration/shared-ui-components/HighImpactChangeIcon';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {GRAY_DARK, GRAY_LIGHTER} from 'ui-library/Colors';

import {ObjectActionType, UpdateAction} from '../../types/objectActions';
import ObjectActionDropdown, {getActionDescription} from '../ObjectActionDropdown';

interface ChildImportPlanSectionProps {
  plan: MigrationObjectPlan;
  isNested?: boolean;
  onUpdatePlan: (plan: MigrationObjectPlan) => void;
  highImpactChangeMap: Map<string, Array<HighImpactChange>>;
}

interface ChildImportPlanSectionWrapperProps {
  children: React.ReactNode;
  title: string;
  isNested?: boolean;
  dataTestAttrs?: DataTestAttrs;
}

export function ChildImportPlanSectionWrapper(props: ChildImportPlanSectionWrapperProps): JSX.Element {
  return (
    <div {...getTestProps(props.dataTestAttrs)}>
      <div
        className={classNames(
          'simpleDivTable-row',
          css(styles.childRow),
          props.isNested ? css(styles.nestedSectionHeader) : css(styles.sectionHeader)
        )}
      >
        <div className="simpleDivTable-cell">
          <strong>{props.title}</strong>
        </div>
      </div>
      {props.children}
    </div>
  );
}

/**
 * For each child, remove the child's selected action and any actions where the target
 * is already archived from the child's additional actions. This is done for surfacing
 * archive actions since they are found in additional actions.
 */
export function getUnselectedUpdateActions(
  childMigrationPlans: Array<BaseMigrationObjectPlan>
): Array<UpdateAction> {
  return childMigrationPlans
    .map((child) =>
      child.additionalActions.filter(
        (action) => !areActionsEqual(action, child.selectedAction) && !action.target.archived
      )
    )
    .flat();
}

export default function ChildImportPlanSection(props: ChildImportPlanSectionProps): JSX.Element {
  return (
    <ChildImportPlanSectionWrapper
      title={OBJECT_TYPE_TO_CHILD_DISPLAY_TEXT[props.plan.objectType as ObjectTypeWithChild]}
      isNested={props.isNested}
    >
      {props.plan.childMigrationPlans.map((child, index) => (
        <ChildImportPlanRow
          key={child.objectId}
          plan={child}
          onUpdateChildPlan={(childPlan: BaseMigrationObjectPlan) => {
            props.onUpdatePlan({
              ...props.plan,
              childMigrationPlans: [
                ...props.plan.childMigrationPlans.slice(0, index),
                childPlan,
                ...props.plan.childMigrationPlans.slice(index + 1),
              ],
            });
          }}
          isNested={props.isNested}
          highImpactChanges={props.highImpactChangeMap.get(child.objectId) || []}
        />
      ))}
      {props.plan.subtractedChildIdentifiers.map((identifier) => {
        const shouldRemove = OBJECT_TYPE_TO_REMOVED_CHILD_CLASS.has(props.plan.objectType);
        return (
          <SubtractedChildToRow
            displayName={identifier.displayName}
            highImpactChanges={props.highImpactChangeMap.get(identifier.provenanceUri) || []}
            key={_.uniqueId('child-')}
            shouldRemove={shouldRemove}
          />
        );
      })}
    </ChildImportPlanSectionWrapper>
  );
}

interface ChildImportPlanRowProps {
  plan: BaseMigrationObjectPlan;
  isNested?: boolean;
  onUpdateChildPlan: (plan: BaseMigrationObjectPlan) => void;
  highImpactChanges: Array<HighImpactChange>;
}

function ChildImportPlanRow(props: ChildImportPlanRowProps): JSX.Element {
  return (
    <div
      className={classNames(
        'simpleDivTable-row',
        props.plan.selectedAction.type === ObjectActionType.IGNORE && css(styles.ignoreRow),
        css(styles.childRow),
        {
          [css(styles.nestedRow)]: props.isNested,
        }
      )}
      key={props.plan.objectId}
    >
      <div
        className={classNames('simpleDivTable-cell', 'u-flex-display u-vertical-center-items', {
          [css(styles.firstCell)]: !props.isNested,
          [css(styles.nestedFirstChildCell)]: props.isNested,
        })}
      >
        <span className={classNames(css(styles.descriptionText))}>{props.plan.objectDisplayName}</span>
        {props.highImpactChanges.map((highImpactChange, index) => (
          <HighImpactChangeIcon key={_.uniqueId('icon-')} highImpactChange={highImpactChange} />
        ))}
      </div>
      <div className={classNames('simpleDivTable-cell', css(styles.secondCell))}>
        {props.plan.initialAction.type === ObjectActionType.UPDATE &&
        props.plan.objectId === props.plan.initialAction.target.provenanceUri ? (
          getActionDescription(props.plan.initialAction)
        ) : (
          <ObjectActionDropdown
            object={props.plan}
            onUpdateObject={props.onUpdateChildPlan}
            excludeIgnore={true}
          />
        )}
      </div>
    </div>
  );
}

interface SubtractedChildToRowProps {
  displayName: string;
  highImpactChanges: Array<HighImpactChange>;
  shouldRemove: boolean;
}

/**
 * A row for a child object that is not found in child migration plans. This means that the child
 * was either removed or archived.
 */
function SubtractedChildToRow(props: SubtractedChildToRowProps): JSX.Element {
  return (
    <div className={classNames('simpleDivTable-row', css(styles.childRow))} key={props.displayName}>
      <div className={classNames('simpleDivTable-cell', css(styles.firstCell))} />
      <div className={classNames('simpleDivTable-cell', css(styles.secondCell))}>
        <span className={css(styles.descriptionText)}>
          {props.shouldRemove ? 'Remove' : 'Archive'} <strong>{props.displayName}</strong>
        </span>
        {props.highImpactChanges.map((highImpactChange) => (
          <HighImpactChangeIcon
            key={_.uniqueId('high-impact-missing-icon-')}
            highImpactChange={highImpactChange}
          />
        ))}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  firstCell: {
    marginLeft: 30,
  },
  // To get the dropdowns in the child section to line up with the parent section
  secondCell: {
    marginLeft: -16,
  },
  sectionHeader: {
    paddingLeft: 42,
  },
  nestedSectionHeader: {
    paddingLeft: '55px',
  },
  ignoreRow: {
    color: GRAY_DARK,
  },
  childRow: {
    backgroundColor: GRAY_LIGHTER,
    border: 'none',
  },
  nestedRow: {
    paddingLeft: '40px',
  },
  nestedFirstChildCell: {
    marginLeft: '14px',
  },
  descriptionText: {
    marginRight: 6,
  },
});
